import * as trace from 'owa-trace';
import commonStrings from 'owa-strings/owa-mini-common-strings';
import EventScope from 'owa-service/lib/contract/EventScope';
import React, { useContext } from 'react';
import { Button, ButtonType } from 'mini-button';
import { createAndShowDeleteConfirmDialog } from '../utils/createAndShowDeleteConfirmDialog';
import { getEventScope } from 'mini-calendar-event-services/lib/utils/getEventScope';
import { ReactAppCtx } from 'mini-app-context';
import { ReactAppSourceContext } from 'data/lib/utils/ReactAppSourceContext';
import { RsvpButtonProps } from '../schema/RsvpButton.types';
import { RsvpServiceState } from '../schema/RsvpServiceState';
import { throwResponseError } from 'mini-calendar-event-services';
import {
    DELETE_OR_CANCEL_EVENT_CONFIRM_CLICKED,
    DELETE_OR_CANCEL_EVENT_COMPLETE,
    DELETE_OR_CANCEL_EVENT_ABORT,
    DELETE_OR_CANCEL_EVENT_CLICKED,
} from 'data/lib/mini-app-constants/commonEventSignalConstants';

export const RsvpRemoveButton = (props: RsvpButtonProps) => {
    const {
        seriesMasterItem,
        item,
        onDeleteServiceCompletion,
        colorScheme,
        highlightRemoveButton,
        getAriaLabel,
        deleteWithoutConfirmation,
        removeCanceledEvent,
    } = props;

    const isSeriesCancelled = !!seriesMasterItem?.canRemove();
    const actionButtonText = commonStrings.removeEventButtonLabel;
    const actionButtonAriaLabel = isSeriesCancelled
        ? commonStrings.removeSeriesButtonAriaLabel
        : commonStrings.removeEventButtonAriaLabel;

    const actionButtonType = highlightRemoveButton ? ButtonType.InlinePrimary : ButtonType.Inline;
    const scope = isSeriesCancelled ? EventScope.Default : getEventScope(item);

    // get the action source context value
    const actionSource = useContext(ReactAppSourceContext);
    const ctx = useContext(ReactAppCtx);

    // callbacks
    const onConfirmClick = () => {
        ctx?.signal(
            DELETE_OR_CANCEL_EVENT_CONFIRM_CLICKED,
            { isUIAction: true },
            { actionSource, isCore: true }
        );
        removeCanceledEvent()
            .then(responseBody => {
                const isSuccess = responseBody?.ResponseClass === 'Success';
                if (isSuccess) {
                    onDeleteServiceCompletion?.(RsvpServiceState.Success, scope);
                } else {
                    throwResponseError(responseBody);
                }
                ctx?.signal(
                    DELETE_OR_CANCEL_EVENT_COMPLETE,
                    undefined /** custom data */,
                    { actionSource },
                    isSuccess
                        ? undefined
                        : { error: new Error('[RsvpRemoveButton]: ' + responseBody.ResponseCode) }
                );
            })
            .catch(error => {
                trace.errorThatWillCauseAlert(error);
                onDeleteServiceCompletion?.(RsvpServiceState.Failed, scope);
                ctx?.signal(
                    DELETE_OR_CANCEL_EVENT_COMPLETE,
                    undefined /** custom data */,
                    { actionSource },
                    {
                        error,
                    }
                );
            });
    };

    const onCancelClick = () => {
        ctx?.signal(DELETE_OR_CANCEL_EVENT_ABORT, {
            isUIAction: true,
            abortSrc: 'DiscardButton',
        });
    };

    const onOverlayClick = () => {
        ctx?.signal(DELETE_OR_CANCEL_EVENT_ABORT, {
            isUIAction: true,
            abortSrc: 'OverlayClick',
        });
    };

    const onClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.stopPropagation();
        ctx?.signal(
            DELETE_OR_CANCEL_EVENT_CLICKED,
            {
                isUIAction: true,
                subSource: 'Rsvp',
            },
            { actionSource }
        );
        deleteWithoutConfirmation
            ? onConfirmClick()
            : createAndShowDeleteConfirmDialog(
                  scope,
                  onConfirmClick,
                  onCancelClick,
                  onOverlayClick,
                  colorScheme
              );
    };

    return (
        <Button
            hasPopup={!item?.IsOrganizer}
            text={actionButtonText}
            type={actionButtonType}
            colorScheme={colorScheme}
            onClick={onClick}
            ariaLabel={getAriaLabel?.(actionButtonAriaLabel) || actionButtonAriaLabel}
            onTouchStart={ev => {
                ev.stopPropagation();
            }}
            onTouchEnd={ev => {
                ev.stopPropagation();
            }}
        />
    );
};
